/**
 * Reconstructs the "Steps Completed by Agent" tracker from RPA audit-trail
 * detail entries.
 *
 * A single order's `identifier` accumulates every workflow and every re-run, so
 * callers must first isolate the submission bot's entries (by `rpa_name`); this
 * module then isolates the latest run (by timestamp gap) and renders every
 * granular step from that run verbatim — no clubbing, grouping, or phasing.
 *
 * @module buildAgentStepsFromAuditTrail
 */
import {
  AgentActionItem,
  AgentStep,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { DetailedEventModel } from "../../../data-model/eventsModal";
import {
  ALERT_STEP_ICON,
  DRAFT_ACTION_ITEMS,
  FAILED_ACTION_ITEMS,
  GENERIC_STEP_ICON,
} from "./submissionAgentStepsConfig";
import { SubmissionDisplayState } from "./submissionDisplayState";

/** A single audit-trail detail entry (timestamp + flattened action metadata). */
type AuditEntry = NonNullable<DetailedEventModel["entries"]>[number];

/** Default gap (ms) that separates two distinct RPA runs of the same bot. */
const RUN_GAP_MS = 15 * 60 * 1000;

/**
 * RPA `action_type` values that are internal navigation/plumbing rather than
 * meaningful workflow steps, so they are hidden from the "Steps Completed by
 * Agent" tracker. Values mirror the backend `ActionType` enum string values.
 */
const SKIP_ACTION_TYPES: ReadonlySet<string> = new Set([
  "navigate",
  "switch_window",
  "wait",
  "switch_to_iframe",
  "switch_to_default",
  "hit_refresh",
  "scroll_vertical",
  "send_keys_globally",
  "hit_enter",
  "extract_order_data",
  "click_javascript",
]);

/**
 * Determines whether an audit entry represents an internal action that should
 * be hidden from the agent steps tracker.
 *
 * @input entry - A single audit-trail detail entry.
 * @returns True when the entry's `action_type` is in {@link SKIP_ACTION_TYPES}.
 */
const isSkippedAction = (entry: AuditEntry): boolean => {
  const actionType = entry.metadata?.action_type;
  return typeof actionType === "string" && SKIP_ACTION_TYPES.has(actionType);
};

/**
 * Parses an audit entry timestamp into epoch millis.
 *
 * @input timestamp - ISO timestamp string from an audit entry.
 * @returns Epoch millis, or NaN when unparseable.
 */
const toMillis = (timestamp: string | undefined): number =>
  timestamp ? new Date(timestamp).getTime() : NaN;

/**
 * Isolates the most recent RPA run from a bot's full entry history.
 *
 * Entries are sorted ascending by timestamp and split wherever the gap between
 * consecutive entries exceeds {@link RUN_GAP_MS}; the last group is returned.
 *
 * @input entries - All detail entries for one order + `rpa_name`.
 * @returns The entries belonging to the latest run, ascending by timestamp.
 */
export const isolateLatestRun = (entries: AuditEntry[]): AuditEntry[] => {
  const sorted = [...entries]
    .filter((entry) => !Number.isNaN(toMillis(entry.timestamp)))
    .sort((a, b) => toMillis(a.timestamp) - toMillis(b.timestamp));

  if (sorted.length === 0) {
    return [];
  }

  let runStartIndex = 0;
  for (let i = 1; i < sorted.length; i += 1) {
    const gap =
      toMillis(sorted[i].timestamp) - toMillis(sorted[i - 1].timestamp);
    if (gap > RUN_GAP_MS) {
      runStartIndex = i;
    }
  }

  return sorted.slice(runStartIndex);
};

/**
 * Returns the last screenshot path in a run (for the "View Screenshot" action).
 *
 * @input entries - Latest-run entries, ascending by timestamp.
 * @returns The most recent `screenshot_path`, or undefined when none exists.
 */
export const getLastScreenshotPath = (
  entries: AuditEntry[],
): string | undefined => {
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const path = entries[i]?.metadata?.screenshot_path;
    if (path) {
      return path as string;
    }
  }
  return undefined;
};

/**
 * Extracts the payer-portal login URL from a run's first navigation step.
 *
 * The submission bot's first `navigate` action opens the payer portal, so its
 * `url` is used as the target for the "Log in to payer portal" links.
 *
 * @input entries - Latest-run entries, ascending by timestamp.
 * @returns The first navigate action's URL, or undefined when none exists.
 */
export const getPortalLoginUrl = (
  entries: AuditEntry[],
): string | undefined => {
  for (const entry of entries) {
    if (entry.metadata?.action_type !== "navigate") {
      continue;
    }
    const url = entry.metadata?.url;
    if (typeof url === "string" && url.length > 0) {
      return url;
    }
  }
  return undefined;
};

/**
 * Extracts the human-readable label for an audit entry.
 *
 * @input entry - A single audit-trail detail entry.
 * @returns The step name, falling back to the action type, else "".
 */
const entryLabel = (entry: AuditEntry): string =>
  (entry.metadata?.name ?? entry.metadata?.action_type ?? "") as string;

/**
 * Normalizes a raw step label for display.
 *
 * Labels that already read like a sentence are passed through untouched; bare
 * snake_case / kebab-case identifiers are expanded into Title Case words.
 *
 * @input label - The raw step label from the audit entry.
 * @returns A trimmed, display-ready label.
 */
const humanizeLabel = (label: string): string => {
  const trimmed = label.trim();
  if (trimmed.length === 0 || trimmed.includes(" ")) {
    return trimmed;
  }
  return trimmed
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
};

/** Inputs required to build the agent steps for a submission. */
export interface BuildAgentStepsArgs {
  displayState: SubmissionDisplayState;
  entries: AuditEntry[];
  /** Payer-portal URL used for the "Log in to payer portal" action link. */
  portalLoginUrl?: string;
}

/**
 * Points every "Log in to payer portal" action item at the resolved portal URL.
 *
 * Action items whose id ends in `-login` carry the portal link; when a portal
 * URL is available it replaces the placeholder `href`, otherwise the item is
 * returned unchanged.
 *
 * @input items - The follow-up action items for a step.
 * @input portalLoginUrl - The resolved payer-portal URL, if any.
 * @returns Action items with the login link resolved.
 */
const withPortalLoginUrl = (
  items: AgentActionItem[],
  portalLoginUrl: string | undefined,
): AgentActionItem[] => {
  if (!portalLoginUrl) {
    return items;
  }
  return items.map((item) =>
    item.id.endsWith("-login") ? { ...item, href: portalLoginUrl } : item,
  );
};

/** Display states that render the agent steps tracker. */
const TRACKER_STATES: ReadonlySet<SubmissionDisplayState> = new Set([
  "automation_completed",
  "submitted_successfully",
  "draft_saved",
  "submission_failed",
]);

/**
 * Builds the agent step list for the submission side panel.
 *
 * Only the states that render a tracker in the design (`automation_completed`,
 * `submitted_successfully`, `draft_saved`, and `submission_failed`) produce
 * steps; every other state returns null. Each entry in the latest RPA run is
 * rendered as its own
 * `completed` step in execution order — steps are shown exactly as the bot
 * logged them, with no grouping onto higher-level phases. Internal
 * navigation/plumbing actions (see {@link SKIP_ACTION_TYPES}) are omitted.
 *
 * For a saved draft a trailing `action-required` step is appended with the
 * follow-up items the human needs to finish. For a failed submission the last
 * logged step (where the bot stopped) is flagged `action-required` in place,
 * since that is the step that needs manual attention.
 *
 * @input args - Display state and the latest-run audit entries.
 * @returns The agent steps to render, or null when the state has no tracker.
 */
export const buildSubmissionAgentSteps = ({
  displayState,
  entries,
  portalLoginUrl,
}: BuildAgentStepsArgs): AgentStep[] | null => {
  if (!TRACKER_STATES.has(displayState)) {
    return null;
  }

  const steps: AgentStep[] = entries
    .filter((entry) => !isSkippedAction(entry))
    .map((entry) => humanizeLabel(entryLabel(entry)))
    .filter((label) => label.length > 0)
    .map((label, index) => ({
      id: `rpa-step-${index}`,
      label,
      icon: GENERIC_STEP_ICON,
      status: "completed" as const,
    }));

  if (displayState === "submission_failed") {
    const failingStep = steps[steps.length - 1];
    if (failingStep) {
      failingStep.icon = ALERT_STEP_ICON;
      failingStep.status = "action-required";
      failingStep.tagLabel = "Action Required";
      failingStep.actionTitle = "This step failed — please complete it";
      failingStep.actionItems = withPortalLoginUrl(
        FAILED_ACTION_ITEMS,
        portalLoginUrl,
      );
    }
    return steps;
  }

  if (displayState === "draft_saved") {
    steps.push({
      id: "draft-action-required",
      label: "Complete the remaining steps",
      icon: GENERIC_STEP_ICON,
      status: "action-required",
      tagLabel: "Action Required",
      actionTitle: "Please Complete these Steps",
      actionItems: withPortalLoginUrl(DRAFT_ACTION_ITEMS, portalLoginUrl),
    });
  }

  return steps;
};
